import { useEffect, useRef, useSyncExternalStore } from "react";
import { computeClient } from "../computeClient";
import { discoverClient, startDiscoverPublicTry } from "../discoverClient";
import type { PersonalApp } from "../types";
import type { AppRunState } from "@/components/runs/useAppRun";
import type { PortablePlacement, PortableImportRecovery } from "@/api/portableApplicationClient";
import { PUBLIC_TRIAL_SECONDS } from "@/app/publicTrialPolicy";
import { launchFailureDetails, type LaunchFailure } from "./launchFailure";

interface TrialSnapshot {
  target: string;
  trialTarget: string | null;
  state: AppRunState;
  instance: PersonalApp | null;
  elapsedSeconds: number;
  busy: boolean;
  expired: boolean;
  limitSeconds: number | null;
  failure: LaunchFailure | null;
}
const demo: AppRunState = { kind: "demo" };

/** One browser view owns at most one start/stop operation across route mounts.
 * A start is allowed to settle after navigation so its returned trial can be
 * reclaimed. Aborting a POST/poll is not evidence that the server stopped it.
 */
export class DiscoverTrialController {
  private snapshot: TrialSnapshot = {
    target: "",
    trialTarget: null,
    state: demo,
    instance: null,
    elapsedSeconds: 0,
    busy: false,
    expired: false,
    limitSeconds: PUBLIC_TRIAL_SECONDS,
    failure: null,
  };
  private listeners = new Set<() => void>();
  private owner: object | null = null;
  private generation = 0;
  private active: {
    app: PersonalApp;
    expiry: number;
    started: number;
    stopRequested: boolean;
    reason: "stopped" | "expired";
    target: string;
    limitSeconds: number | null;
  } | null = null;
  private starting = false;
  private uncertain = false;
  private recoverAllocation: (() => Promise<PortableImportRecovery>) | null =
    null;
  private checking = false;
  private nextCheck = 0;
  private request: AbortController | null = null;
  private stopping = false;
  private timer: ReturnType<typeof setInterval> | null = null;

  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    if (!this.timer) this.timer = setInterval(() => this.tick(), 250);
    return () => {
      this.listeners.delete(listener);
      if (
        !this.listeners.size &&
        !this.active &&
        !this.uncertain &&
        this.timer
      ) {
        clearInterval(this.timer);
        this.timer = null;
      }
    };
  };
  private publish(patch: Partial<TrialSnapshot>) {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }
  bind(owner: object, target: string) {
    if (this.owner === owner && this.snapshot.target === target) return;
    this.owner = owner;
    this.generation++;
    this.request?.abort();
    this.publish({
      target,
      failure: null,
      expired: false,
      state:
        this.starting || this.uncertain
          ? { kind: "queued", message: "Finishing the previous Try…" }
          : this.active
            ? { kind: "stopping" }
            : demo,
      instance: null,
      elapsedSeconds: 0,
      busy: this.starting || Boolean(this.active) || this.uncertain,
    });
    this.tick();
    if (this.active) {
      this.request?.abort();
      void this.stop();
    }
  }
  release(owner: object) {
    if (this.owner !== owner) return;
    this.owner = null;
    this.generation++;
    this.request?.abort();
    if (this.active) {
      this.request?.abort();
      void this.stop();
    }
  }
  async start(owner: object, id: string, publicGuest = false, resolveGpuPlacement?: (signal: AbortSignal) => Promise<PortablePlacement>) {
    this.tick();
    if (
      this.owner !== owner ||
      this.starting ||
      this.stopping ||
      this.active ||
      this.uncertain ||
      (this.snapshot.failure?.retryAt ?? 0) > Date.now()
    )
      return;
    const generation = this.generation;
    this.starting = true;
    this.publish({
      state: { kind: "queued", message: "Starting your Try…" },
      busy: true,
      instance: null,
      elapsedSeconds: 0,
      trialTarget: this.snapshot.target,
      expired: false,
      failure: null,
    });
    const request = new AbortController();
    let startupDeadline = setTimeout(() => request.abort(), 180_000);
    this.request = request;
    try {
      const app = await startDiscoverPublicTry(id, request.signal, {
        publicGuest,
        ...(resolveGpuPlacement ? { resolveGpuPlacement: async (signal: AbortSignal) => {
          clearTimeout(startupDeadline);
          startupDeadline = setTimeout(() => request.abort(), 12 * 60_000);
          const placement = await resolveGpuPlacement(signal);
          signal.throwIfAborted();
          clearTimeout(startupDeadline);
          startupDeadline = setTimeout(() => request.abort(), 12 * 60_000);
          return placement;
        }} : {}),
        onPending: (recover) => {
          this.recoverAllocation = recover;
        },
        onProgress: (message) => {
          if (
            generation === this.generation &&
            this.owner === owner &&
            this.starting &&
            !this.uncertain
          )
            this.publish({
              state: { kind: "queued", message },
              busy: true,
            });
        },
        onAllocated: (allocated) => {
          this.rememberAllocation(allocated);
          if (generation !== this.generation || this.owner !== owner) {
            request.abort();
            void this.stop();
          }
        },
      });
      const parsed = Date.parse(app.public_try_expires_at ?? "");
      const limit = app.public_try_limit_seconds === null ? null : app.public_try_limit_seconds ?? PUBLIC_TRIAL_SECONDS;
      const expiry = limit === null ? Infinity : Number.isFinite(parsed) ? parsed : Date.now() + limit * 1_000;
      this.rememberAllocation(app, true);
      this.recoverAllocation = null;
      this.starting = false;
      if (
        generation !== this.generation ||
        this.owner !== owner ||
        expiry <= Date.now()
      ) {
        await this.stop(expiry <= Date.now() ? "expired" : "stopped");
        return;
      }
      this.publish({
        instance: app,
        busy: false,
        limitSeconds: limit,
        state: {
          kind: "ready",
          appUrl: app.app_url,
          embedPolicy: "sandboxed",
          limitSeconds: limit,
        },
      });
      this.tick();
    } catch (cause) {
      this.starting = false;
      if (cause instanceof DOMException && cause.name === "AbortError" && !this.recoverAllocation && !this.active) {
        this.publish({ busy: false, state: demo, failure: null, trialTarget: null });
        return;
      }
      const failure = launchFailureDetails(cause);
      if ((cause as { allocationNone?: boolean })?.allocationNone === true)
        this.recoverAllocation = null;
      if (generation === this.generation && this.owner === owner)
        this.publish({ failure });
      if (this.active) {
        if (!this.stopping) await this.stop();
        return;
      }
      if (
        (cause as { code?: string }).code === "public_try_ended" &&
        !this.recoverAllocation
      ) {
        this.publish({
          busy: false,
          instance: null,
          trialTarget: null,
          state: { kind: "ended", reason: "expired" },
          expired: generation === this.generation && this.owner === owner,
        });
        return;
      }
      // Metadata/validation failures precede allocation and are retryable.
      // Once the allocating POST may have been sent, even a 4xx response must
      // be reconciled: an error is not proof that no lease became claimable.
      if (this.recoverAllocation) {
        this.uncertain = true;
        this.ensureTimer();
        this.publish({
          busy: true,
          instance: null,
          state: {
            kind: "queued",
            message:
              "Could not confirm the Try. Waiting for the server to confirm its release…",
          },
        });
        return;
      }
      if (generation === this.generation && this.owner === owner) {
        this.publish({
          busy: false,
          trialTarget: null,
          state: {
            kind: "error",
            message: failure.message,
            retryable: failure.retryable,
          },
        });
      } else if (!this.active)
        this.publish({ state: demo, busy: false, trialTarget: null });
    } finally {
      clearTimeout(startupDeadline);
    }
  }
  private ensureTimer() {
    if (!this.timer) this.timer = setInterval(() => this.tick(), 250);
  }
  private rememberAllocation(app: PersonalApp, ready = false) {
    const expiry = Date.parse(app.public_try_expires_at ?? "");
    const limit = app.public_try_limit_seconds === null ? null : app.public_try_limit_seconds ?? PUBLIC_TRIAL_SECONDS;
    if (this.active?.app.instance_id === app.instance_id) {
      if (ready) {
        // Allocation carries the preparation deadline. The verified response
        // supplies the actual Try deadline and its guest-scoped stop handle.
        this.active.app = app;
        this.active.expiry = limit === null ? Infinity : Number.isFinite(expiry) ? expiry : Date.now() + limit * 1_000;
        this.active.started = limit !== null && Number.isFinite(expiry) ? expiry - limit * 1_000 : Date.now();
        this.active.limitSeconds = limit;
      }
      return;
    }
    this.active = {
      app,
      expiry: limit === null ? Infinity : Number.isFinite(expiry) ? expiry : Date.now() + limit * 1_000,
      started: limit !== null && Number.isFinite(expiry) ? expiry - limit * 1_000 : Date.now(),
      limitSeconds: limit,
      stopRequested: false,
      reason: "stopped",
      target: this.snapshot.trialTarget ?? this.snapshot.target,
    };
    this.ensureTimer();
  }
  private completeStop(active: NonNullable<DiscoverTrialController["active"]>) {
    if (this.active !== active) return;
    this.active = null;
    this.recoverAllocation = null;
    if (!this.listeners.size && this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.publish({
      state: active.target === this.snapshot.target ? { kind: "ended", reason: active.reason } : demo,
      instance: null,
      trialTarget: null,
      busy: this.starting,
    });
  }
  cancelStart = (owner: object) => {
    if (this.owner !== owner) return;
    this.generation++;
    this.request?.abort();
    if (this.active) void this.stop();
    // A delayed POST still settles/reconciles through start's existing owner
    // fence. Retry remains blocked until the server confirms resource release.
  };
  async stop(reason: "stopped" | "expired" = "stopped") {
    if (!this.active || this.stopping) return;
    const active = this.active;
    active.stopRequested = true;
    if (reason === "expired") active.reason = reason;
    this.stopping = true;
    this.publish({ state: { kind: "stopping" }, instance: null, busy: true });
    try {
      const result = active.app.public_guest_try
        ? await discoverClient.stopPublicGuestTry(active.app)
        : await computeClient.stop(active.app.instance_id);
      // HTTP success, sleeping, and the TTL are not teardown receipts. An
      // older API omits this field and deliberately keeps the allocation held.
      if (result.public_try_cleanup?.resources_released === true)
        this.completeStop(active);
    } catch {
      if (this.active === active)
        this.publish({
          state: { kind: "stop_failed" },
          busy: true,
          instance: null,
        });
    } finally {
      this.stopping = false;
      this.nextCheck = Date.now() + 1_000;
    }
  }
  private async checkRelease() {
    if (this.checking || this.stopping) return;
    this.checking = true;
    const pendingActive = this.active;
    try {
      if (this.uncertain && this.recoverAllocation) {
        const recovered = await this.recoverAllocation();
        if (recovered.status === "allocated") {
          this.uncertain = false;
          this.rememberAllocation(recovered.app);
          await this.stop();
        } else if (recovered.status === "unallocated") {
          this.uncertain = false;
          this.recoverAllocation = null;
          if (!this.listeners.size && this.timer) {
            clearInterval(this.timer);
            this.timer = null;
          }
          this.publish({
            busy: false,
            trialTarget: null,
            state:
              this.snapshot.trialTarget === this.snapshot.target
                ? {
                    kind: "error",
                    message: this.snapshot.failure?.message ?? "The Try could not start. Try again.",
                    retryable: this.snapshot.failure?.retryable ?? true,
                  }
                : demo,
          });
        }
      } else if (this.active?.stopRequested) {
        const active = this.active;
        const result = active.app.public_guest_try
          ? await discoverClient.stopPublicGuestTry(active.app)
          : await computeClient.get(active.app.instance_id);
        if (result.public_try_cleanup?.resources_released === true)
          this.completeStop(active);
      }
    } catch {
      if (pendingActive && this.active === pendingActive)
        this.publish({ state: { kind: "stop_failed" }, busy: true });
    } finally {
      this.checking = false;
      this.nextCheck = Date.now() + 2_000;
    }
  }
  tick = () => {
    if (
      Date.now() >= this.nextCheck &&
      (this.uncertain || this.active?.stopRequested)
    )
      void this.checkRelease();
    const active = this.active;
    if (!active) return;
    if (active.expiry <= Date.now()) {
      active.reason = "expired";
      if (
        this.snapshot.instance ||
        !this.snapshot.busy ||
        this.snapshot.elapsedSeconds !== active.limitSeconds
      )
        this.publish({ instance: null, busy: true, elapsedSeconds: active.limitSeconds!,
          expired: active.target === this.snapshot.target });
      if (!active.stopRequested) void this.stop("expired");
      return;
    }
    const elapsedSeconds = Math.max(
      0,
      Math.min(active.limitSeconds ?? Infinity, Math.floor((Date.now() - active.started) / 1_000)),
    );
    if (elapsedSeconds !== this.snapshot.elapsedSeconds)
      this.publish({ elapsedSeconds });
  };
  recheck = () => {
    this.tick();
    if (this.snapshot.state.kind === "stop_failed") void this.stop();
    else void this.checkRelease();
  };
}

const controller = new DiscoverTrialController();
export function useDiscoverTrial({
  listingId,
  userId,
  canTry,
  allowPublic = false,
  resolveGpuPlacement,
  onSignIn,
}: {
  listingId: string | null;
  userId: string | null;
  canTry: boolean;
  allowPublic?: boolean;
  resolveGpuPlacement?: (signal: AbortSignal) => Promise<PortablePlacement>;
  onSignIn: () => void;
}) {
  const owner = useRef<object>({});
  const target = `${userId ?? "signed-out"}:${listingId ?? "none"}`;
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
  );
  useEffect(() => {
    controller.bind(owner.current, target);
  }, [target]);
  useEffect(() => {
    const token = owner.current;
    const recheck = () => controller.recheck();
    window.addEventListener("online", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      window.removeEventListener("online", recheck);
      document.removeEventListener("visibilitychange", recheck);
      controller.release(token);
    };
  }, []);
  const visible = snapshot.target === target;
  return {
    state: visible ? snapshot.state : demo,
    failure: visible ? snapshot.failure : null,
    instance: visible ? snapshot.instance : null,
    elapsedSeconds: visible ? snapshot.elapsedSeconds : 0,
    expired: visible && snapshot.expired,
    limitSeconds: snapshot.limitSeconds,
    remainingSeconds: snapshot.limitSeconds === null ? null : Math.max(0, snapshot.limitSeconds - (visible ? snapshot.elapsedSeconds : 0)),
    busy: snapshot.busy || !visible,
    activity: snapshot.trialTarget?.startsWith(`${userId ?? "signed-out"}:`)
      ? {
          listingId: snapshot.trialTarget.slice(
            (userId ?? "signed-out").length + 1,
          ),
          state: snapshot.state.kind,
        }
      : null,
    start: () => {
      if (!userId && !allowPublic) onSignIn();
      else if (listingId && canTry)
        void controller.start(owner.current, listingId, !userId && allowPublic, resolveGpuPlacement);
    },
    stop: () => controller.stop(),
    cancelStart: () => controller.cancelStart(owner.current),
  };
}
